import { MUSE_SERVICE_UUID_16, MuseError, systemTimeSource, toCanonicalCharacteristicUuid } from "@musejs/core";
//#region src/transport.ts
/**
* Notifications smaller than this mean the link is stuck at the default MTU.
*
* The default ATT MTU is 23 bytes, leaving 20 for a notification payload. Athena
* packets reach 244, so a link that never delivers more than 20 bytes is
* silently truncating everything.
*/
const DEFAULT_MTU_PAYLOAD = 20;
/**
* Web Bluetooth transport.
*
* ## What the platform makes hard
*
* **Scanning needs a user gesture.** `requestDevice()` may only be called from a
* click or keypress. Called from anywhere else it rejects, so {@link scan}
* reports that as `TRANSPORT_UNAVAILABLE` with an explanation rather than
* hanging or failing obscurely.
*
* **There is no MTU accessor.** The connection therefore infers it from the
* largest notification seen, and raises `MTU_TOO_SMALL` if nothing bigger than a
* default-MTU payload arrives while streaming. That is the difference between
* "this browser cannot carry Athena packets" and "the data looks corrupt".
*
* **Only Chromium browsers implement any of this.** Firefox and Safari have both
* declined; there is no workaround, and pretending otherwise would waste a
* user's afternoon.
*/
var WebBluetoothTransport = class WebBluetoothTransport {
	name = "web-bluetooth";
	#time;
	#service;
	#mtuProbeSeconds;
	#devices = /* @__PURE__ */ new Map();
	constructor(options = {}) {
		this.#time = options.timeSource ?? systemTimeSource;
		this.#service = options.serviceUuid ?? MUSE_SERVICE_UUID_16;
		this.#mtuProbeSeconds = options.mtuProbeSeconds ?? 5;
	}
	/** Whether this browser implements Web Bluetooth at all. */
	static get supported() {
		return typeof navigator !== "undefined" && navigator.bluetooth !== void 0;
	}
	/**
	* Whether a usable Bluetooth adapter is actually available.
	*
	* Distinct from {@link supported}: Chrome implements the API on every
	* platform, then reports `false` here when there is no adapter, when the
	* adapter is off, or when the browser itself has been denied Bluetooth access
	* by the operating system. That last case is the one worth catching early —
	* on macOS it produces no chooser and a bare console line, which looks like
	* the page is broken.
	*
	* Returns `null` where the browser is too old to implement the check, since
	* "unknown" and "unavailable" call for different messages.
	*/
	static async available() {
		if (!WebBluetoothTransport.supported) return false;
		const bluetooth = navigator.bluetooth;
		if (typeof bluetooth.getAvailability !== "function") return null;
		try {
			return await bluetooth.getAvailability();
		} catch {
			return null;
		}
	}
	/**
	* Devices already permitted, plus a chooser when called from a user gesture.
	*
	* Previously permitted devices come back without any prompt, which is what
	* makes "reconnect to my headband" possible on a page reload.
	*/
	async *scan(options = {}) {
		const bluetooth = this.#bluetooth();
		if (options.signal?.aborted === true) return;
		const seen = /* @__PURE__ */ new Set();
		const permitted = await this.#permittedDevices(bluetooth);
		for (const device of permitted) {
			seen.add(device.id);
			this.#devices.set(device.id, device);
			yield this.#describe(device);
		}
		if (seen.size > 0) return;
		let chosen;
		try {
			chosen = await bluetooth.requestDevice({
				filters: [{ services: [this.#service] }],
				optionalServices: [this.#service]
			});
		} catch (cause) {
			throw MuseError.wrap(cause, "TRANSPORT_UNAVAILABLE", describeChooserFailure(cause));
		}
		this.#devices.set(chosen.id, chosen);
		yield this.#describe(chosen);
	}
	async open(deviceId, options = {}) {
		options.signal?.throwIfAborted();
		const bluetooth = this.#bluetooth();
		let device = this.#devices.get(deviceId);
		if (device === void 0) for (const known of await this.#permittedDevices(bluetooth)) {
			this.#devices.set(known.id, known);
			if (known.id === deviceId) device = known;
		}
		if (device === void 0) throw new MuseError("DEVICE_NOT_FOUND", `No permitted device with id ${deviceId}. Web Bluetooth only reopens devices the user has already chosen.`);
		const gatt = device.gatt;
		if (gatt === void 0) throw new MuseError("UNSUPPORTED_DEVICE", `Device ${deviceId} exposes no GATT server`);
		let server;
		try {
			server = await gatt.connect();
		} catch (cause) {
			throw MuseError.wrap(cause, "CONNECTION_LOST", `Could not connect to ${deviceId}`);
		}
		let characteristics;
		try {
			characteristics = await (await server.getPrimaryService(this.#service)).getCharacteristics();
		} catch (cause) {
			server.disconnect();
			throw MuseError.wrap(cause, "UNSUPPORTED_DEVICE", `Device ${deviceId} does not expose the Muse service`);
		}
		return new WebBluetoothConnection(device, server, characteristics, this.#time, this.#mtuProbeSeconds);
	}
	async #permittedDevices(bluetooth) {
		const getDevices = bluetooth.getDevices;
		if (typeof getDevices !== "function") return [];
		try {
			return await getDevices.call(bluetooth);
		} catch {
			return [];
		}
	}
	#describe(device) {
		return {
			id: device.id,
			name: device.name ?? null,
			rssi: null,
			discoveredAt: this.#time.now()
		};
	}
	#bluetooth() {
		if (typeof navigator === "undefined" || navigator.bluetooth === void 0) throw new MuseError("TRANSPORT_UNAVAILABLE", "This browser does not implement Web Bluetooth. Only Chromium-based browsers do — Firefox and Safari have both declined to implement it. Use the Node transport instead.");
		return navigator.bluetooth;
	}
};
/**
* A live Web Bluetooth link.
*
* Adds two things the platform-neutral `Connection` cannot express: the inferred
* MTU and the characteristics the device turned out to have. Both matter here
* specifically because Web Bluetooth hides them.
*/
var WebBluetoothConnection = class {
	id;
	closed;
	#server;
	#characteristics;
	#time;
	#mtuProbeUs;
	/** Cleanup for every live subscription, so closing can detach them all. */
	#subscriptions = /* @__PURE__ */ new Set();
	#settle;
	#settled = false;
	#closedByCaller = false;
	#largestNotification = 0;
	#firstNotificationAt = null;
	#device;
	#onDisconnected;
	/** Live listeners per characteristic, so one unsubscribe does not silence the rest. */
	#notifying = /* @__PURE__ */ new Map();
	constructor(device, server, characteristics, time, mtuProbeSeconds) {
		this.id = device.id;
		this.#server = server;
		this.#time = time;
		this.#mtuProbeUs = mtuProbeSeconds * 1e6;
		this.#characteristics = new Map(characteristics.map((c) => [toCanonicalCharacteristicUuid(c.uuid), c]));
		this.closed = new Promise((resolve) => {
			this.#settle = resolve;
		});
		this.#device = device;
		this.#onDisconnected = () => {
			this.#finish({ kind: this.#closedByCaller ? "requested" : "device" });
		};
		device.addEventListener("gattserverdisconnected", this.#onDisconnected);
	}
	/**
	* Inferred from traffic, because Web Bluetooth exposes no MTU.
	*
	* `null` until something arrives. Once notifications are flowing, this is the
	* largest notification payload seen — a lower bound on the link's payload
	* capacity, which is ATT MTU minus 3. That is the same quantity the Node
	* transport reports (see `Connection.mtu`), so the two are comparable.
	*/
	get mtu() {
		return this.#largestNotification === 0 ? null : this.#largestNotification;
	}
	/**
	* True once enough time has passed with nothing but tiny notifications.
	*
	* A link stuck at the 23-byte default MTU truncates every Athena packet, and
	* the symptom is data that decodes into nonsense rather than an error.
	*/
	get mtuLooksTooSmall() {
		if (this.#firstNotificationAt === null) return false;
		if (this.#largestNotification > DEFAULT_MTU_PAYLOAD) return false;
		return this.#time.now() - this.#firstNotificationAt > this.#mtuProbeUs;
	}
	async subscribe(characteristic, listener) {
		const target = this.#require(characteristic);
		const onChange = (event) => {
			const arrivalTime = this.#time.now();
			const value = event.target.value;
			if (value === void 0) return;
			const data = new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
			if (data.length > this.#largestNotification) this.#largestNotification = data.length;
			this.#firstNotificationAt ??= arrivalTime;
			if (this.mtuLooksTooSmall) {
				this.#failMtuTooSmall();
				return;
			}
			listener(data, arrivalTime);
		};
		const key = toCanonicalCharacteristicUuid(characteristic);
		target.addEventListener("characteristicvaluechanged", onChange);
		try {
			await target.startNotifications();
		} catch (cause) {
			target.removeEventListener("characteristicvaluechanged", onChange);
			throw MuseError.wrap(cause, "CONNECTION_LOST", `Could not subscribe to ${key}`);
		}
		this.#notifying.set(key, (this.#notifying.get(key) ?? 0) + 1);
		const unsubscribe = () => {
			if (!this.#subscriptions.delete(unsubscribe)) return;
			target.removeEventListener("characteristicvaluechanged", onChange);
			const remaining = (this.#notifying.get(key) ?? 1) - 1;
			if (remaining > 0) {
				this.#notifying.set(key, remaining);
				return;
			}
			this.#notifying.delete(key);
			target.stopNotifications().catch(() => void 0);
		};
		this.#subscriptions.add(unsubscribe);
		return unsubscribe;
	}
	async write(characteristic, data, options = {}) {
		const target = this.#require(characteristic);
		const payload = Uint8Array.from(data);
		try {
			if (options.withResponse === true) await target.writeValueWithResponse(payload);
			else await target.writeValueWithoutResponse(payload);
		} catch (cause) {
			throw MuseError.wrap(cause, "CONNECTION_LOST", `Write to ${toCanonicalCharacteristicUuid(characteristic)} failed`);
		}
	}
	async close() {
		if (this.#settled) return;
		this.#closedByCaller = true;
		for (const unsubscribe of [...this.#subscriptions]) unsubscribe();
		this.#server.disconnect();
		this.#finish({ kind: "requested" });
	}
	/** Every characteristic the Muse service exposes, canonicalised. */
	get availableCharacteristics() {
		return [...this.#characteristics.keys()].toSorted();
	}
	#require(characteristic) {
		if (this.#settled) throw new MuseError("CONNECTION_LOST", "Connection is closed");
		const found = this.#characteristics.get(toCanonicalCharacteristicUuid(characteristic));
		if (found !== void 0) return found;
		throw new MuseError("UNSUPPORTED_DEVICE", `Device does not expose ${toCanonicalCharacteristicUuid(characteristic)}`, { details: { available: this.availableCharacteristics } });
	}
	#failMtuTooSmall() {
		const error = new MuseError("MTU_TOO_SMALL", `Nothing larger than ${DEFAULT_MTU_PAYLOAD} bytes arrived in ${this.#mtuProbeUs / 1e6} s. The link is at the default MTU, which truncates every Athena packet; this browser or adapter cannot carry them.`, { details: { largestNotification: this.#largestNotification } });
		this.#finish({
			kind: "error",
			error
		});
		this.#server.disconnect();
	}
	#finish(reason) {
		if (this.#settled) return;
		this.#settled = true;
		this.#device.removeEventListener("gattserverdisconnected", this.#onDisconnected);
		for (const unsubscribe of [...this.#subscriptions]) unsubscribe();
		this.#settle(reason);
	}
};
function webBluetoothTransport(options = {}) {
	return new WebBluetoothTransport(options);
}
/**
* Says what actually stopped the chooser appearing.
*
* `requestDevice()` rejects for several unrelated reasons and the browser
* distinguishes them by `DOMException.name`. Collapsing them into one guess —
* this used to blame "secure context and user gesture" every time — sends
* people to check things that were never wrong: `localhost` *is* a secure
* context, and a click handler *is* a gesture. Chrome meanwhile logs the real
* cause to the console, where the library was not looking.
*
* Where the state is knowable, it is checked rather than assumed.
*/
function describeChooserFailure(cause) {
	const name = cause instanceof DOMException ? cause.name : "";
	const detail = cause instanceof Error && cause.message !== "" ? ` Browser said: ${cause.message}` : "";
	if (typeof globalThis.isSecureContext === "boolean" && !globalThis.isSecureContext) return `Web Bluetooth needs a secure context. This page is served over plain HTTP from a host that is not localhost, so the chooser cannot open. Use HTTPS or localhost.${detail}`;
	switch (name) {
		case "NotFoundError": return `No headband was chosen. Either the picker was dismissed, or no device advertising the Muse service was in range — check the headband is on and not already connected elsewhere.${detail}`;
		case "SecurityError": return `The browser blocked Web Bluetooth for this page. Common causes: Bluetooth is disallowed in the site's permissions (Chrome: the icon at the left of the address bar → Site settings), a Permissions-Policy header, or an embedding iframe without allow="bluetooth".${detail}`;
		case "NotAllowedError": return `Permission to use Bluetooth was refused. If no chooser appeared at all, the browser itself lacks Bluetooth access rather than the page: on macOS, grant it under System Settings → Privacy & Security → Bluetooth. Otherwise the call did not come from a click or keypress, which Web Bluetooth requires.${detail}`;
		case "InvalidStateError": return `The Bluetooth adapter is not ready. Check it is switched on.${detail}`;
		default: return `The device chooser could not be shown (${name || "unknown error"}).${detail}`;
	}
}
//#endregion
export { WebBluetoothConnection, WebBluetoothTransport, webBluetoothTransport };

//# sourceMappingURL=index.js.map